/**
 * Keeping the menu badges true (QA C, 2026-10-16).
 *
 * The badges were counted once, at sign-in, and after the person's own actions: a janazah waiting for a second
 * person, sent from another office, did not show until somebody reloaded the page — and that badge is the one place in
 * the panel where minutes matter (fault 58). Now they are counted again every minute while the tab is open, and at
 * once when the window gets focus again (the volunteer comes back to the tab), but not twice within a few seconds.
 *
 * Plain functions with the window and the clock passed in, so a test can drive them without a browser.
 */
export const BADGE_REFRESH_MS = 60000;
/** A focus right after a refresh does not ask again. */
export const FOCUS_MIN_GAP_MS = 5000;

type Target = { addEventListener: (t: string, f: () => void) => void; removeEventListener: (t: string, f: () => void) => void };
type Timers = { setInterval: (f: () => void, ms: number) => unknown; clearInterval: (h: unknown) => void };

/** Calls `refresh` every `everyMs` and when `target` gets focus; returns the function that stops both. */
export function keepFresh(refresh: () => void, target: Target, timers: Timers = globalThis as unknown as Timers,
  { everyMs = BADGE_REFRESH_MS, minGapMs = FOCUS_MIN_GAP_MS, now = () => Date.now() } = {}): () => void {
  let last = now();
  const run = () => { last = now(); refresh(); };
  const onFocus = () => { if (now() - last >= minGapMs) run(); };
  const h = timers.setInterval(run, everyMs);
  target.addEventListener('focus', onFocus);
  return () => { timers.clearInterval(h); target.removeEventListener('focus', onFocus); };
}

/** Banners waiting for THIS person's approval: the drafts whose `actions` (core banners.allowed) include «approve». */
export const bannersToApprove = (items: unknown): number =>
  (Array.isArray(items) ? items : []).filter((b) => b && Array.isArray(b.actions) && b.actions.includes('approve')).length;
